import { useState } from 'react';
import { Button, Input } from '@cuito/ui';
import { locationsApi } from '../../api/locations';
import { readServerMessage } from '../../lib/apiError';
import {
  clampGridSize,
  shelvesOutOfGrid,
  SHELF_GRID_MAX,
  SHELF_GRID_MIN,
  type ShelfBox,
} from '../../lib/shelfForm';

interface Props {
  locationId: string;
  rows: number;
  cols: number;
  /** 该地点已有货架：用来在缩小前就说清楚哪几个货架会越界 */
  shelves: readonly ShelfBox[];
  onSaved: (next: { rows: number; cols: number }) => void;
}

/**
 * 坐标底板行列数设置（`05-ui-design-system.md` §3.2 `/locations/:id/settings`、§2.2.2 R10）。
 *
 * 从旧 `admin/ShelfSettings.tsx` 的「只配列数（3–15 夹取）」改为「行数 + 列数各自 1–20」。
 * **缩小是有代价的**：已有货架若越界，服务端回 400 `SHELF_OUT_OF_GRID`。
 * 这里提前算一遍并点名是哪几个货架，但仍把服务端的原文当权威（真被拒时原样显示）。
 */
export default function ShelfGridSetting({ locationId, rows, cols, shelves, onSaved }: Props) {
  const [rowText, setRowText] = useState(String(rows));
  const [colText, setColText] = useState(String(cols));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const nextRows = clampGridSize(Number(rowText));
  const nextCols = clampGridSize(Number(colText));
  const blocked = shelvesOutOfGrid(shelves, nextRows, nextCols);
  const dirty = nextRows !== rows || nextCols !== cols;

  const save = async (): Promise<void> => {
    if (blocked.length > 0) {
      setNotice(null);
      setError(
        `缩小到 ${nextRows} × ${nextCols} 后，货架 ${blocked
          .map((shelf) => shelf.code)
          .join('、')} 会超出坐标底板，请先拆分或删除这些货架`,
      );
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await locationsApi.update(locationId, { shelfRows: nextRows, shelfCols: nextCols });
      setNotice(`坐标底板已保存为 ${nextRows} × ${nextCols}`);
      onSaved({ rows: nextRows, cols: nextCols });
    } catch (e) {
      setError(readServerMessage(e) ?? '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex max-w-xl flex-col gap-3 rounded-lg border-2 border-black p-4">
      <h2 className="text-lg font-bold text-gray-900">坐标底板</h2>

      {error && (
        <p className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
          {error}
        </p>
      )}
      {notice && (
        <p className="rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">{notice}</p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Input
          label={`行数（${SHELF_GRID_MIN}–${SHELF_GRID_MAX}）`}
          className="w-32"
          inputMode="numeric"
          value={rowText}
          onChange={(e) => setRowText(e.target.value)}
          onBlur={() => setRowText(String(clampGridSize(Number(rowText))))}
        />
        <Input
          label={`列数（${SHELF_GRID_MIN}–${SHELF_GRID_MAX}）`}
          className="w-32"
          inputMode="numeric"
          value={colText}
          onChange={(e) => setColText(e.target.value)}
          onBlur={() => setColText(String(clampGridSize(Number(colText))))}
        />
        <Button onClick={() => void save()} disabled={saving || !dirty} className="disabled:opacity-50">
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>

      <p className="text-sm text-gray-500">
        格子边长不再随容器缩小：算下来不足 44px 时改为横向滚动（`05-ui-design-system.md` §4.5）。
      </p>
    </section>
  );
}
